// ./src/components/features/Events/controls/GuestModeCallout.tsx
//
// A prominent, mode-specific explanation shown under the "How guests join"
// selector. The one-line help below the segmented control is easy to miss, so
// this Alert spells out what each mode actually does — especially Invite list
// (you build a guest list + send invitations) and Save the date (an info-only
// announcement page, no RSVP / tickets). Shared by the details editor and the
// Create Event wizard.

import React from 'react';
import { Alert, Stack, Group, Text, Button, Box, ThemeIcon, AspectRatio } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { AppIcon } from '@/components/generic';

import { GUEST_MODE, DEFAULT_GUEST_MODE, GUEST_MODE_VIDEOS, toYouTubeEmbed } from './guestModeConfig';

export interface GuestModeCalloutProps {
  mode?: string | null;
  /** Invite-list only: jump to the event's guest list to add people. */
  onManageGuests?: () => void;
  /** Optional YouTube walkthrough URL — overrides the GUEST_MODE_VIDEOS default. */
  videoUrl?: string | null;
}

/**
 * ONE colour for every mode. This was previously per-mode (grape / violet /
 * blue / indigo), which had no meaning — nothing else in the app maps a guest
 * mode to a colour — but read as one: grape+violet vs blue+indigo grouped the
 * four tabs into two visual pairs. `blue` is this app's informational-Alert
 * colour (red = error, green = success, yellow = warning, blue = info); the mode
 * is already identified by its icon and heading.
 */
const CALLOUT_COLOR = 'blue';

interface CalloutConfig {
  icon: string;
  title: string;
  /** Ordered steps (numbered) — used for Invite list. */
  steps?: string[];
  /** Plain body paragraph — used for the simpler modes. */
  body?: string;
}

export const GuestModeCallout: React.FC<GuestModeCalloutProps> = ({ mode, onManageGuests, videoUrl }) => {
  const { t } = useTranslation(['events', 'common']);
  const value = mode ?? DEFAULT_GUEST_MODE;
  const embedUrl = toYouTubeEmbed(videoUrl ?? GUEST_MODE_VIDEOS[value]);

  const config: Record<string, CalloutConfig> = {
    [GUEST_MODE.SAVE_THE_DATE]: {
      icon: 'CalendarEvent',
      title: t('events:guestModeCallout.saveTheDateTitle', { defaultValue: 'An announcement page — no RSVPs or tickets yet' }),
      body: t('events:guestModeCallout.saveTheDateBody', {
        defaultValue:
          'This publishes a simple public page for your event — cover image, the key details and any text or media you add. There are no RSVP buttons and nothing is for sale. You can still gather interest and preferences, and switch to Open RSVP or Tickets whenever you’re ready.',
      }),
    },
    [GUEST_MODE.MANAGED_INVITES]: {
      icon: 'Send',
      title: t('events:guestModeCallout.inviteListTitle', { defaultValue: 'You invite each guest — three steps' }),
      steps: [
        t('events:guestModeCallout.inviteStep1', { defaultValue: 'Build your guest list — add each person with a name and an email and/or phone.' }),
        t('events:guestModeCallout.inviteStep2', { defaultValue: 'Send invitations from the platform — each guest gets their own private link.' }),
        t('events:guestModeCallout.inviteStep3', { defaultValue: 'Only people you invited can see the event and respond — anyone else who opens its website sees just its name and that it’s private. Volunteering and sponsorship sign-ups are for your guests too.' }),
      ],
    },
    [GUEST_MODE.LINK_RSVP]: {
      icon: 'World',
      title: t('events:guestModeCallout.openRsvpTitle', { defaultValue: 'Anyone with the link can RSVP' }),
      body: t('events:guestModeCallout.openRsvpBody', {
        defaultValue: 'Share your event link anywhere — social, chat or email — and anyone who opens it can RSVP. No guest list is needed up front.',
      }),
    },
    [GUEST_MODE.TICKETED]: {
      icon: 'Ticket',
      title: t('events:guestModeCallout.ticketedTitle', { defaultValue: 'Guests check out to reserve a spot' }),
      body: t('events:guestModeCallout.ticketedBody', {
        defaultValue: 'Sell free or paid tickets with limits and sales windows. Set up tickets, add-ons, taxes and fees in the selling settings below.',
      }),
    },
  };

  const c = config[value];
  if (!c) return null;

  return (
    <Alert color={CALLOUT_COLOR} variant="light" radius="md" p="md">
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <ThemeIcon color={CALLOUT_COLOR} variant="light" size={34} radius="md">
          <AppIcon iconName={c.icon} size={19} />
        </ThemeIcon>
        <Stack gap={8} style={{ flex: 1, minWidth: 0 }}>
          <Text fw={700} size="md">
            {c.title}
          </Text>

          {/* Same md + full contrast as the numbered steps below — every mode's
              explanation gets ONE treatment. This is the primary description of
              what a mode does, not secondary metadata, and it never reflects a
              selected/unselected state. It used to be dimmed here but not on the
              steps, which made the four tabs look inconsistent. */}
          {c.body && <Text size="md">{c.body}</Text>}

          {c.steps && (
            <Stack gap={6}>
              {c.steps.map((step, i) => (
                <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
                  <ThemeIcon color={CALLOUT_COLOR} variant="filled" size={20} radius="xl" style={{ flexShrink: 0 }}>
                    <Text fw={700} size="md" c="white">
                      {i + 1}
                    </Text>
                  </ThemeIcon>
                  <Text size="md" style={{ flex: 1 }}>
                    {step}
                  </Text>
                </Group>
              ))}
            </Stack>
          )}

          {value === GUEST_MODE.MANAGED_INVITES && onManageGuests && (
            <Box>
              <Button
                variant="light"
                color={CALLOUT_COLOR}
                size="md"
                mt={2}
                leftSection={<AppIcon iconName="Users" size={16} />}
                onClick={onManageGuests}
              >
                {t('events:guestModeCallout.openGuestList', { defaultValue: 'Open guest list' })}
              </Button>
            </Box>
          )}

          {embedUrl && (
            <Stack gap={6} mt={4}>
              <Group gap={6} wrap="nowrap">
                <AppIcon iconName="Video" size={16} />
                <Text fw={600} size="md">
                  {t('events:guestModeCallout.watchHow', { defaultValue: 'Watch how it works' })}
                </Text>
              </Group>
              <AspectRatio ratio={16 / 9} maw={560} style={{ borderRadius: 'var(--mantine-radius-md)', overflow: 'hidden' }}>
                <iframe
                  src={embedUrl}
                  title={c.title}
                  style={{ border: 0 }}
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </AspectRatio>
            </Stack>
          )}
        </Stack>
      </Group>
    </Alert>
  );
};
